import { z } from 'zod';
import { pageMetaSchema } from './common.js';
import { skuMatchStatusSchema } from './asset.js';

/**
 * SKU catalog DTOs.
 *
 * Mirrors db/schema/303_views_sku.sql (v_sku_catalog):
 *   one row per sku, joined to asset_class + manufacturer + the immutable
 *   launch price + a count of currently non-retired assets per sku.
 */

// SIL-773: master/variant grain (SIL-766/787) — 'variant' covers both a
// standalone SKU (parentSkuId null) and an actual child (parentSkuId set);
// 'master' is a model-family row one or more variants point at.
export const skuGrainSchema = z.enum(['variant', 'master']);
export type SkuGrain = z.infer<typeof skuGrainSchema>;

export const skuCatalogItemSchema = z.object({
  skuId: z.string().uuid(),
  assetClassId: z.string(),
  assetClassName: z.string(),
  manufacturerId: z.string(),
  manufacturerName: z.string(),
  verifiedSku: z.string(),
  modelName: z.string(),
  generation: z.string().nullable(),
  subClass: z.string().nullable(),
  launchDate: z.string().nullable(),
  shortDescription: z.string().nullable(),
  confidenceLevel: skuMatchStatusSchema,
  oemBuybackProgramName: z.string().nullable(),
  listPriceUsd: z.coerce.number().nullable(),
  listPriceEffectiveFrom: z.string().nullable(),
  activeAssetCount: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
  oemSpecVerifiedAt: z.string().nullable(),
  activeOemSpecId: z.string().uuid().nullable(),
  hasOemSpecs: z.boolean(),
  // RUF-289: sku_kind + market comp baseline from v_sku_pricing join
  skuKind: z.enum(['oem', 'generic', 'custom_build']).nullable(),
  baselinePriceUsd: z.coerce.number().nullable(),
  // RUF-294: uncertainty interval — error-propagated for builds, widened for sparse
  p25PriceUsd: z.coerce.number().nullable(),
  p75PriceUsd: z.coerce.number().nullable(),
  sampleSize: z.number().int().nullable(),
  isSparse: z.boolean().nullable(),
  priceBasis: z.string().nullable(),
  // RUF-465: non-unique BOM-composition grouping key for custom_build SKUs
  // (null for oem/generic). Optional — only the SKU detail endpoint populates it.
  bomCompositionKey: z.string().nullable().optional(),
  // SIL-773 (schema: SIL-766/787, SIL-768/789): master/variant hierarchy +
  // family price range. rangeLow/rangeHighUsd are null for grain='variant'.
  grain: skuGrainSchema,
  parentSkuId: z.string().uuid().nullable(),
  rangeLowUsd: z.coerce.number().nullable(),
  rangeHighUsd: z.coerce.number().nullable(),
  // SIL-773: count of live (non-retired) children — only populated by the
  // catalog list endpoint's grouped view; optional elsewhere (bundle
  // components, detail) where it isn't computed.
  variantCount: z.number().int().nullable().optional(),
  // SIL-782: for a variant row (grain='variant', parentSkuId set), the
  // parent master's verifiedSku/displayName — carried via a self-join on
  // v_sku_catalog in the list query so the catalog can name+link the parent
  // without an N+1. Null for a master or a standalone (no parentSkuId).
  // Optional — only the catalog list endpoint populates it.
  parentVerifiedSku: z.string().nullable().optional(),
  parentDisplayName: z.string().nullable().optional(),
});
export type SkuCatalogItem = z.infer<typeof skuCatalogItemSchema>;

export const skuListFilterSchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
  asset_class: z.string().optional(),
  asset_class_id: z.string().optional(),
  manufacturer: z.string().optional(),
  q: z.string().min(1).optional(),
  oem_spec_unverified: z.coerce.boolean().optional(),
  // RUF-297: price-sort and price-range filter via sku_pricing_snapshot
  sort_by: z.enum(['id_desc', 'price_asc', 'price_desc']).optional(),
  min_price: z.coerce.number().positive().optional(),
  max_price: z.coerce.number().positive().optional(),
  // RUF-465: list all SKUs sharing this BOM-composition key (custom_build siblings).
  bomCompositionKey: z.string().optional(),
  // SIL-773: grain filter. Omitted → grouped view (masters + root standalones,
  // i.e. parent_sku_id IS NULL). 'master' | 'variant' filter to that grain
  // exactly (note 'variant' includes both standalones and actual children);
  // 'all' removes the grouping filter entirely (flat list, every row).
  grain: z.enum(['master', 'variant', 'all']).optional(),
});
export type SkuListFilter = z.infer<typeof skuListFilterSchema>;

export const skuListResponseSchema = z.object({
  data: z.array(skuCatalogItemSchema),
  meta: pageMetaSchema,
});
export type SkuListResponse = z.infer<typeof skuListResponseSchema>;

export const pricingRowSchema = z.object({
  sourceId: z.string(), // 'ebay' | 'cdw' | 'all'
  retailerName: z.string().nullable(),
  conditionBand: z.string().nullable(), // 'like_new' | 'normal_enterprise_wear' | 'unknown'
  lotType: z.enum(['individual', 'lot']),
  medianPriceUsd: z.number(),
  p25PriceUsd: z.number(),
  p75PriceUsd: z.number(),
  compCount: z.number().int(),
  outlierCount: z.number().int(),
  lastSeenAt: z.string(), // ISO timestamp
});

export const skuPricingStatsSchema = z.object({
  windowDays: z.number().int().positive(),
  rows: z.array(pricingRowSchema),
});

export type PricingRow = z.infer<typeof pricingRowSchema>;
export type SkuPricingStats = z.infer<typeof skuPricingStatsSchema>;

// CPU + rack_system OEM spec fields (rendered by asset class on SKU detail).
const cpuRackSpecFields = {
  cpuCores: z.coerce.number().nullable(),
  cpuThreads: z.coerce.number().nullable(),
  baseClockGhz: z.coerce.number().nullable(),
  boostClockGhz: z.coerce.number().nullable(),
  cpuSocket: z.string().nullable(),
  cpuMaxSockets: z.coerce.number().nullable(),
  l3CacheMb: z.coerce.number().nullable(),
  rackUnits: z.coerce.number().nullable(),
  maxPowerKw: z.coerce.number().nullable(),
  totalGpuMemoryGb: z.coerce.number().nullable(),
  gpuModel: z.string().nullable(),
  memoryType: z.string().nullable(),
} as const;

export const specHistoryEntrySchema = z.object({
  scrapedAt: z.string(),
  sourceUrl: z.string().nullable(),
  sourceType: z.enum(['html', 'pdf']).nullable(),
  vendor: z.string().nullable(),
  vramGb: z.coerce.number().nullable(),
  tdpWatts: z.coerce.number().nullable(),
  architecture: z.string().nullable(),
  interconnectType: z.string().nullable(),
  formFactor: z.string().nullable(),
  maxGpuCount: z.coerce.number().nullable(),
  cpuSocketCount: z.coerce.number().nullable(),
  pcieGen: z.coerce.number().nullable(),
  maxRamGb: z.coerce.number().nullable(),
  dimmSlots: z.coerce.number().nullable(),
  powerSupplyWatts: z.coerce.number().nullable(),
  memoryBandwidthGbps: z.coerce.number().nullable(),
  fp16Tflops: z.coerce.number().nullable(),
  ...cpuRackSpecFields,
  specFacts: z.record(z.union([z.string(), z.number(), z.boolean()])),
});
export type SpecHistoryEntry = z.infer<typeof specHistoryEntrySchema>;

// activeSpec — latest scrape or verified OEM spec row
export const activeSpecSchema = z.object({
  vramGb: z.coerce.number().nullable(),
  tdpWatts: z.coerce.number().nullable(),
  architecture: z.string().nullable(),
  fp16Tflops: z.coerce.number().nullable(),
  memoryBandwidthGbps: z.coerce.number().nullable(),
  interconnectType: z.string().nullable(),
  formFactor: z.string().nullable(),
  maxGpuCount: z.coerce.number().nullable(),
  cpuSocketCount: z.coerce.number().nullable(),
  pcieGen: z.coerce.number().nullable(),
  maxRamGb: z.coerce.number().nullable(),
  dimmSlots: z.coerce.number().nullable(),
  powerSupplyWatts: z.coerce.number().nullable(),
  ...cpuRackSpecFields,
  scrapedAt: z.string(),
  specFacts: z.record(z.union([z.string(), z.number(), z.boolean()])),
});
export type ActiveSpec = z.infer<typeof activeSpecSchema>;

// SIL-773: minimal cross-linking shape for a master's variant list / a
// variant's parent link — deliberately not the full skuCatalogItemSchema
// (the detail page only needs enough to render a row/link, not a second
// full catalog item).
export const skuVariantSummarySchema = z.object({
  skuId: z.string().uuid(),
  verifiedSku: z.string(),
  displayName: z.string(),
  baselinePriceUsd: z.coerce.number().nullable(),
});
export type SkuVariantSummary = z.infer<typeof skuVariantSummarySchema>;

export const skuDetailResponseSchema = z.object({
  detail: skuCatalogItemSchema,
  pricingStats: skuPricingStatsSchema.nullable(),
  specHistory: z.array(specHistoryEntrySchema),
  activeSpec: activeSpecSchema.nullable(),
  currentSpecFacts: z.record(z.union([z.string(), z.number(), z.boolean()])),
  specDisplaySchema: z.unknown(),
  // SIL-773: grain='master' → its live variants (empty array if none yet);
  // grain='variant' → null. Never both populated.
  variants: z.array(skuVariantSummarySchema).nullable(),
  // SIL-773: grain='variant' with a parent → that master's link; otherwise null.
  parent: skuVariantSummarySchema.nullable(),
});
export type SkuDetailResponse = z.infer<typeof skuDetailResponseSchema>;

// -----------------------------------------------------------------------------
// SIL-844: GET /api/skus/families — family-grain view + exception worklist
// -----------------------------------------------------------------------------

// First-match-wins precedence (packages/shared/src/sku/family.ts computeVerdict):
//   no variants -> spread>=10x -> spread>=5x -> no band -> no config stated
//   -> single variant -> coherent.
export const skuVerdictSchema = z.enum([
  'no_variants',
  'incoherent_defect',
  'incoherent_attention',
  'no_band',
  'no_config_stated',
  'single_variant',
  'coherent',
]);
export type SkuVerdict = z.infer<typeof skuVerdictSchema>;

export const skuFamilyVariantSchema = z.object({
  skuId: z.string().uuid(),
  verifiedSku: z.string(),
  displayName: z.string(),
  baselinePriceUsd: z.coerce.number().nullable(),
  compCount: z.number().int(),
  // class_attributes = '{}' — presence check only, never rendered as "the spec".
  hasNoConfig: z.boolean(),
  // SIL-844 exception section 6 — see isScrapedListingIdentity.
  isScrapedListingIdentity: z.boolean(),
});
export type SkuFamilyVariant = z.infer<typeof skuFamilyVariantSchema>;

export const skuFamilySchema = z.object({
  skuId: z.string().uuid(),
  verifiedSku: z.string(),
  displayName: z.string(),
  manufacturerId: z.string(),
  manufacturerName: z.string(),
  assetClassId: z.string(),
  assetClassName: z.string(),
  rangeLowUsd: z.coerce.number().nullable(),
  rangeHighUsd: z.coerce.number().nullable(),
  sampleSize: z.number().int().nullable(),
  // SIL-836 sentinel (exception section 8) — a master must never hold a
  // direct market_comp. Always expected to be 0; a nonzero value here is the
  // regression this view exists to catch.
  directCompCount: z.number().int(),
  variants: z.array(skuFamilyVariantSchema),
  // Derived server-side (never in the browser) — see family.ts.
  spread: z.number().nullable(),
  verdict: skuVerdictSchema,
});
export type SkuFamily = z.infer<typeof skuFamilySchema>;

// A `grain='variant'` row with no parent master (parent_sku_id IS NULL).
// `expectsFamily` false for exactly the FLAT_ASSET_CLASSES five — a true
// value here is exception section 2 (defect); false is section 9
// (unparented, correctly so — not a defect, do not "fix").
export const skuStandaloneSchema = z.object({
  skuId: z.string().uuid(),
  verifiedSku: z.string(),
  displayName: z.string(),
  manufacturerId: z.string(),
  manufacturerName: z.string(),
  assetClassId: z.string(),
  assetClassName: z.string(),
  baselinePriceUsd: z.coerce.number().nullable(),
  compCount: z.number().int(),
  hasNoConfig: z.boolean(),
  isScrapedListingIdentity: z.boolean(),
  expectsFamily: z.boolean(),
});
export type SkuStandalone = z.infer<typeof skuStandaloneSchema>;

export const skuFamiliesResponseSchema = z.object({
  families: z.array(skuFamilySchema),
  standalones: z.array(skuStandaloneSchema),
});
export type SkuFamiliesResponse = z.infer<typeof skuFamiliesResponseSchema>;

export const updateSkuBodySchema = z.object({
  modelName: z.string().min(1).optional(),
  generation: z.string().nullable().optional(),
  subClass: z.string().nullable().optional(),
  launchDate: z.string().nullable().optional(),
  shortDescription: z.string().nullable().optional(),
  oemBuybackProgramName: z.string().nullable().optional(),
  skuKind: z.enum(['oem', 'generic', 'custom_build']).optional(),
});
export type UpdateSkuBody = z.infer<typeof updateSkuBodySchema>;

export const updateSkuResponseSchema = z.object({ detail: skuCatalogItemSchema });
export type UpdateSkuResponse = z.infer<typeof updateSkuResponseSchema>;

// ── Soft-retire (RUF-328 UI) ────────────────────────────────────────────────
// v_sku_catalog excludes retired SKUs by design (WHERE retired_at IS NULL), so
// the retired-list endpoint queries `sku` directly and gets its own item shape.

export const retireSkuBodySchema = z.object({ retired: z.boolean() });
export type RetireSkuBody = z.infer<typeof retireSkuBodySchema>;

export const retireSkuResponseSchema = z.object({
  skuId: z.string().uuid(),
  retiredAt: z.string().nullable(),
});
export type RetireSkuResponse = z.infer<typeof retireSkuResponseSchema>;

// ── SIL-1117: promote / merge a probe-created SKU ───────────────────────────
// A probe_observed row is unreviewed by construction (auto-created from an
// inventory-probe Hardware Declaration, never oem, never priced). An operator
// either promotes it (flips sku_kind to generic — never oem — and makes it
// catalog-visible/priceable) or merges it into an existing catalog SKU.

export const promoteProbeSkuResponseSchema = z.object({
  skuId: z.string().uuid(),
  skuKind: z.literal('generic'),
  promotedAt: z.string(),
  promotedByUserId: z.string(),
});
export type PromoteProbeSkuResponse = z.infer<typeof promoteProbeSkuResponseSchema>;

export const mergeProbeSkuBodySchema = z.object({ targetSkuId: z.string().uuid() });
export type MergeProbeSkuBody = z.infer<typeof mergeProbeSkuBodySchema>;

export const mergeProbeSkuResponseSchema = z.object({
  skuId: z.string().uuid(),
  targetSkuId: z.string().uuid(),
  mergedAssetCount: z.number().int(),
  promotedAt: z.string(),
  promotedByUserId: z.string(),
});
export type MergeProbeSkuResponse = z.infer<typeof mergeProbeSkuResponseSchema>;

export const retiredSkuItemSchema = z.object({
  skuId: z.string().uuid(),
  assetClassId: z.string(),
  manufacturerId: z.string(),
  manufacturerName: z.string(),
  verifiedSku: z.string(),
  modelName: z.string(),
  retiredAt: z.string(),
  supersededBy: z.string().uuid().nullable(),
});
export type RetiredSkuItem = z.infer<typeof retiredSkuItemSchema>;

export const retiredSkuListResponseSchema = z.object({
  items: z.array(retiredSkuItemSchema),
});
export type RetiredSkuListResponse = z.infer<typeof retiredSkuListResponseSchema>;

export const skuBundleItemSchema = z.object({
  id: z.string().uuid(),
  bundleSkuId: z.string().uuid(),
  componentSkuId: z.string().uuid(),
  quantity: z.number().int().positive(),
  sortOrder: z.number().int(),
  notes: z.string().nullable(),
  componentDetail: skuCatalogItemSchema,
  createdAt: z.string(),
});
export type SkuBundleItem = z.infer<typeof skuBundleItemSchema>;

export const bundleComponentsResponseSchema = z.object({
  components: z.array(skuBundleItemSchema),
  sumOfComponentsUsd: z.number().nullable(),
});
export type BundleComponentsResponse = z.infer<typeof bundleComponentsResponseSchema>;

export const skuScrapeTargetSchema = z.object({
  id: z.string().uuid(),
  skuId: z.string().uuid(),
  url: z.string(),
  sourceType: z.enum(['html', 'pdf']),
  enabled: z.boolean(),
  notes: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SkuScrapeTarget = z.infer<typeof skuScrapeTargetSchema>;

export const scrapeTargetsResponseSchema = z.object({
  targets: z.array(skuScrapeTargetSchema),
});
export type ScrapeTargetsResponse = z.infer<typeof scrapeTargetsResponseSchema>;

// ---------------------------------------------------------------------------
// OEM config component minting (RUF-295)
// ---------------------------------------------------------------------------

/**
 * Inline component spec supplied when creating an oem-kind bundle SKU.
 * The API upserts by (manufacturer_id, verified_sku) — if a SKU with that
 * pair already exists it is reused; otherwise a new oem SKU is minted.
 *
 * `verifiedSku` is optional (RUF-421): when the naming library can compute a
 * canonical identity for `assetClassId` from `gpuModel`/`formFactor`/`vramGb`
 * (currently only the `gpu` template), the computed canonical value wins over
 * any client-supplied raw string — this is what keeps dedup correct across
 * differently-typed raw strings for the same physical part. Otherwise the
 * client-supplied `verifiedSku` is kept as-is, falling back to a mutable
 * `GEN-XXXXXXXX` placeholder only if the client didn't supply one either.
 */
export const oemComponentSpecSchema = z.object({
  manufacturerId: z.string().min(1),
  verifiedSku: z.string().min(1).optional(),
  assetClassId: z.string().min(1),
  modelName: z.string().min(1),
  quantity: z.number().int().positive(),
  sortOrder: z.number().int().optional(),
  notes: z.string().nullable().optional(),
  /** RUF-421: naming-lib identity inputs — only the `gpu` template is implemented today. */
  gpuModel: z.string().nullable().optional(),
  formFactor: z.string().nullable().optional(),
  vramGb: z.union([z.string(), z.number()]).nullable().optional(),
});
export type OemComponentSpec = z.infer<typeof oemComponentSpecSchema>;

export const mintedComponentSchema = z.object({
  componentSkuId: z.string().uuid(),
  verifiedSku: z.string(),
  minted: z.boolean(), // true = newly created, false = reused existing
});
export type MintedComponent = z.infer<typeof mintedComponentSchema>;

export const createSkuBodySchema = z.object({
  assetClassId: z.string(),
  manufacturerId: z.string().optional(),
  verifiedSku: z.string().optional(),
  modelName: z.string().min(1),
  skuKind: z.enum(['oem', 'generic', 'custom_build']).optional(),
  shortDescription: z.string().nullable().optional(),
  listPriceUsd: z.number().positive().optional(),
  /**
   * RUF-421: naming-lib identity inputs for the main mint path — only the
   * `gpu` template is implemented today. When present and identity-complete
   * for an implemented template, the computed canonical name wins over any
   * client-supplied `verifiedSku`/`modelName`.
   */
  gpuModel: z.string().nullable().optional(),
  formFactor: z.string().nullable().optional(),
  vramGb: z.union([z.string(), z.number()]).nullable().optional(),
  /** OEM config minting (RUF-295): inline component specs. Only valid when skuKind = 'oem'. */
  components: z.array(oemComponentSpecSchema).optional(),
  /**
   * SIL-773: optional master SKU this new SKU is a variant of. The
   * sku_parent_grain_guard trigger (787) enforces validity (parent must be a
   * non-retired root sharing manufacturer_id + asset_class_id, depth capped
   * at 2) — the API surfaces its errors as 422, not a raw 500.
   */
  parentSkuId: z.string().uuid().nullable().optional(),
});
export type CreateSkuBody = z.infer<typeof createSkuBodySchema>;

export const createBundleItemBodySchema = z.object({
  componentSkuId: z.string().uuid(),
  quantity: z.number().int().positive(),
  sortOrder: z.number().int().optional(),
  notes: z.string().nullable().optional(),
});
export type CreateBundleItemBody = z.infer<typeof createBundleItemBodySchema>;

export const updateBundleItemBodySchema = z.object({
  quantity: z.number().int().positive().optional(),
  sortOrder: z.number().int().optional(),
  notes: z.string().nullable().optional(),
});
export type UpdateBundleItemBody = z.infer<typeof updateBundleItemBodySchema>;

// ---------------------------------------------------------------------------
// OEM Spec verify/unverify (RUF-163)
// ---------------------------------------------------------------------------

export const verifyOemSpecBodySchema = z.object({
  verified: z.boolean(),
});
export type VerifyOemSpecBody = z.infer<typeof verifyOemSpecBodySchema>;

export const createScrapeTargetBodySchema = z.object({
  url: z.string(),
  sourceType: z.enum(['html', 'pdf']),
  notes: z.string().nullable().optional(),
});
export type CreateScrapeTargetBody = z.infer<typeof createScrapeTargetBodySchema>;

export const updateScrapeTargetBodySchema = z.object({
  url: z.string().optional(),
  sourceType: z.enum(['html', 'pdf']).optional(),
  enabled: z.boolean().optional(),
  notes: z.string().nullable().optional(),
});
export type UpdateScrapeTargetBody = z.infer<typeof updateScrapeTargetBodySchema>;

// ---------------------------------------------------------------------------
// SKU suggestion (RUF-325)
// ---------------------------------------------------------------------------

export const skuSuggestionSchema = z.object({
  skuId: z.string().uuid(),
  verifiedSku: z.string(),
  modelName: z.string().nullable(),
  vramGb: z.number().nullable(),
  subClass: z.string().nullable(),
  score: z.number(),
  reason: z.string(),
  /** SIL-1116 — true when the matched SKU is retired: still a valid match, comps are frozen. */
  retired: z.boolean(),
  // SIL-1535 — display-only family/variant context, mirrors v_sku_catalog's
  // grain/parentVerifiedSku/parentDisplayName so a suggestion row can show
  // "variant of X" or a family badge the same way the search picker does.
  grain: z.enum(['master', 'variant']).nullable().optional(),
  parentVerifiedSku: z.string().nullable().optional(),
  parentDisplayName: z.string().nullable().optional(),
});
export type SkuSuggestion = z.infer<typeof skuSuggestionSchema>;

export const skuSuggestionsResponseSchema = z.object({
  suggestions: z.array(skuSuggestionSchema),
});
export type SkuSuggestionsResponse = z.infer<typeof skuSuggestionsResponseSchema>;

// ---------------------------------------------------------------------------
// Model-name suggest (Add-Asset typeahead)
// ---------------------------------------------------------------------------

export const modelSuggestItemSchema = z.object({
  skuId: z.string().uuid(),
  modelName: z.string(),
  assetClassName: z.string().nullable(),
  hasPricing: z.boolean(),
  // SIL-774: master/variant grain (SIL-766/787) — step-one suggestions are
  // masters + standalone variants only; picking a 'master' row drives a
  // second model-suggest fetch (?parentSkuId=<that skuId>) for its variants.
  grain: skuGrainSchema,
  parentSkuId: z.string().uuid().nullable(),
  // A6 family/model picker (SIL-953 prototype adoption): point price + a
  // master's family range, straight off v_sku_catalog/sku_pricing_snapshot
  // (SIL-768, migration 789) — no extra query. rangeLowUsd/rangeHighUsd are
  // always null for grain='variant' rows.
  baselinePriceUsd: z.coerce.number().nullable(),
  rangeLowUsd: z.coerce.number().nullable(),
  rangeHighUsd: z.coerce.number().nullable(),
  // Comp sample size backing baselinePriceUsd/the range — already resolved
  // onto v_sku_catalog (sku_pricing_snapshot.sample_size), so this costs no
  // extra join over the point-price fields above.
  sampleSize: z.number().nullable(),
  // Live (non-retired) child count — only meaningful for grain='master' rows;
  // null for variants. Lets the UI label a product-line option with its
  // configuration count without a second per-master fetch.
  variantCount: z.number().nullable(),
});
export type ModelSuggestItem = z.infer<typeof modelSuggestItemSchema>;

export const modelSuggestResponseSchema = z.object({
  models: z.array(modelSuggestItemSchema),
});
export type ModelSuggestResponse = z.infer<typeof modelSuggestResponseSchema>;

// ---------------------------------------------------------------------------
// List Price History (RUF-88)
// ---------------------------------------------------------------------------

export const listPriceEntrySchema = z.object({
  id: z.string().uuid(),
  skuId: z.string().uuid(),
  listPriceUsd: z.coerce.number(),
  effectiveFrom: z.string(),
  source: z.string().nullable(),
  notes: z.string().nullable(),
  setAt: z.string(),
  setByUserId: z.string().nullable(),
  isReferenced: z.boolean(),
});
export type ListPriceEntry = z.infer<typeof listPriceEntrySchema>;

export const listPricesResponseSchema = z.object({
  entries: z.array(listPriceEntrySchema),
});
export type ListPricesResponse = z.infer<typeof listPricesResponseSchema>;

export const createListPriceBodySchema = z.object({
  listPriceUsd: z.number().positive(),
  effectiveFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD'),
  source: z.string().optional(),
  notes: z.string().optional(),
});
export type CreateListPriceBody = z.infer<typeof createListPriceBodySchema>;

// ---------------------------------------------------------------------------
// ECCN classification (SIL-641)
//
// Deliberately its own ops-only response shape — NOT folded into
// skuCatalogItemSchema/v_sku_catalog, which is client-visible. Visibility
// enforcement is a separate ticket (SIL-648); until then, ECCN never rides
// along on a customer-facing response.
// ---------------------------------------------------------------------------

export const eccnCodeSchema = z.object({
  code: z.string(),
  description: z.string(),
  restrictionRank: z.number().int(),
  licenseRequired: z.boolean(),
});
export type EccnCode = z.infer<typeof eccnCodeSchema>;

export const eccnCodesResponseSchema = z.object({
  codes: z.array(eccnCodeSchema),
});
export type EccnCodesResponse = z.infer<typeof eccnCodesResponseSchema>;

/**
 * Effective ECCN detail for one sku (GET /api/skus/:skuId/eccn), derived
 * from v_sku_eccn. For a composed (bundle) sku, `effectiveEccn` is the
 * MAX(restriction_rank) component's eccn (577/626 composition path);
 * `ownEccn` is always this sku's own row value, ignored for composed skus'
 * effective value but still shown for transparency. `isUnclassified` is
 * true whenever `effectiveEccn` is null — a composed sku with even one
 * unclassified component is unclassified as a whole (fail-closed).
 */
export const skuEccnDetailSchema = z.object({
  skuId: z.string().uuid(),
  isComposed: z.boolean(),
  componentCount: z.number().int().nullable(),
  ownEccn: z.string().nullable(),
  eccnConfidenceLevel: skuMatchStatusSchema,
  eccnDataSourceId: z.string().nullable(),
  eccnClassifiedByUserId: z.string().nullable(),
  eccnClassifiedAt: z.string().nullable(),
  effectiveEccn: z.string().nullable(),
  effectiveRestrictionRank: z.number().int().nullable(),
  isUnclassified: z.boolean(),
});
export type SkuEccnDetail = z.infer<typeof skuEccnDetailSchema>;

export const skuEccnDetailResponseSchema = z.object({ detail: skuEccnDetailSchema });
export type SkuEccnDetailResponse = z.infer<typeof skuEccnDetailResponseSchema>;

/**
 * PATCH /api/skus/:skuId/eccn body. `eccn: null` clears the classification
 * back to unmatched (confidenceLevel forced server-side to 'unmatched').
 * Only meaningful for a LEAF sku — composed skus derive their effective
 * value from components and are not directly classifiable via this route
 * (the API rejects the call with a 422 for a composed sku; classify its
 * components instead).
 */
export const classifySkuEccnBodySchema = z.object({
  eccn: z.string().nullable(),
  confidenceLevel: skuMatchStatusSchema.optional(),
  dataSourceId: z.string().nullable().optional(),
});
export type ClassifySkuEccnBody = z.infer<typeof classifySkuEccnBodySchema>;

export const skuEccnCoverageItemSchema = z.object({
  skuId: z.string().uuid(),
  assetClassId: z.string(),
  assetClassName: z.string(),
  manufacturerId: z.string(),
  manufacturerName: z.string(),
  verifiedSku: z.string(),
  modelName: z.string(),
  skuKind: z.enum(['oem', 'generic', 'custom_build']).nullable(),
  isComposed: z.boolean(),
  componentCount: z.number().int().nullable(),
  activeAssetCount: z.number().int(),
});
export type SkuEccnCoverageItem = z.infer<typeof skuEccnCoverageItemSchema>;

export const skuEccnCoverageResponseSchema = z.object({
  items: z.array(skuEccnCoverageItemSchema),
  totalUnclassified: z.number().int(),
  totalSkus: z.number().int(),
  coveragePercent: z.number(),
});
export type SkuEccnCoverageResponse = z.infer<typeof skuEccnCoverageResponseSchema>;
